import { AlertTriangle, Globe, Lock, Mail, RefreshCw } from "lucide-react";

import {
  Badge,
  Button,
  cx,
  ErrorAlert,
  Section,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  type Requirement,
} from "@/components/ui";
import type { DnsConflict, DnsRecord, Zone } from "@/lib/domains";

import { fullHost, recordValue, shortTime, touchesEmail, touchesWebsite } from "./util";

/** Figma 4724-6466 "Records needing attention (Advanced)": records waiting on the provider and conflicts. */
export function AttentionSection({
  zone,
  fqdn,
  editRequires,
  onRecheck,
  rechecking,
  recheckError,
  onRemove,
}: {
  zone: Zone;
  fqdn: string;
  /** The permission fixing a conflict needs; null when records can't be edited here at all. */
  editRequires: Requirement | null;
  onRecheck: () => void;
  rechecking: boolean;
  recheckError: unknown;
  onRemove: (records: DnsRecord[], kept: DnsRecord) => void;
}) {
  const waiting = zone.records.filter((r) => r.status !== "live");
  const byId = new Map(zone.records.map((r) => [r.id, r]));

  return (
    <Section
      title="Records needing attention (Advanced)"
      action={
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-1.5 text-xs text-fg-subtle">
            <RefreshCw aria-hidden className={cx("size-3.5", rechecking && "animate-spin")} />
            Zone last confirmed {shortTime(zone.zone_confirmed_at)}
          </span>
          <Button size="lg" variant="secondary" loading={rechecking} onClick={onRecheck}>
            Recheck now
          </Button>
        </div>
      }
    >
      <ErrorAlert error={recheckError} />
      {waiting.map((r) => (
        <WaitingCard key={r.id} record={r} />
      ))}
      {zone.conflicts.map((c) => {
        const records = c.record_ids.map((id) => byId.get(id)).filter((r): r is DnsRecord => Boolean(r));
        if (records.length < 2) return null;
        return (
          <ConflictCard
            key={`${c.kind}-${c.name}-${c.record_ids.join()}`}
            conflict={c}
            records={records}
            fqdn={fqdn}
            editRequires={editRequires}
            onRemove={onRemove}
          />
        );
      })}
    </Section>
  );
}

function TypeChip({ type }: { type: string }) {
  return (
    <span className="rounded-md bg-[#221c19] px-1.5 py-0.5 text-[11px] font-semibold text-white">{type}</span>
  );
}

function WaitingCard({ record: r }: { record: DnsRecord }) {
  const failed = r.status === "error";
  const email = touchesEmail(r);
  const website = touchesWebsite(r);
  return (
    <div
      className={cx(
        "flex flex-col gap-3 rounded-[14px] border p-4",
        failed ? "border-[#ffc4bb] bg-[#fff1ee]" : "border-[#f4b53f] bg-[#fef4e5]",
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <TypeChip type={r.type} />
        <p className="min-w-0 flex-1 text-sm font-semibold break-all text-fg">
          {r.name} → {recordValue(r)} · TTL {r.ttl}
        </p>
        <Badge size="md" tone={failed ? "danger" : "warning"}>
          {failed ? "Failed" : "Pending"}
        </Badge>
      </div>
      <p className="text-[13px] text-[#5c5551]">
        Added{r.created_by_email ? ` by ${r.created_by_email}` : ""} at {shortTime(r.updated_at)}.{" "}
        {failed
          ? `The DNS provider didn't accept it${r.error ? `: ${r.error.replace(/\.$/, "")}` : ""}. Recheck now to send it again.`
          : "Waiting for the DNS provider to confirm. This usually takes a few minutes."}
      </p>
      {(email || website) && (
        <div className="flex flex-col gap-2">
          {website && (
            <ImpactLine
              icon="website"
              tone="neutral"
              title="Website"
              detail={`Visitors to ${r.name === "@" ? "the domain" : r.name} are sent here once this record is live.`}
            />
          )}
          {email && (
            <ImpactLine
              icon="email"
              tone="neutral"
              title="Email"
              detail="Email changes take effect once this record is live."
            />
          )}
        </div>
      )}
    </div>
  );
}

function ImpactLine({
  icon,
  tone,
  title,
  detail,
}: {
  icon: "website" | "email";
  tone: "danger" | "success" | "neutral";
  title: string;
  detail: string;
}) {
  const Icon = icon === "website" ? Globe : Mail;
  return (
    <div className="flex gap-2">
      <Icon
        aria-hidden
        className={cx(
          "mt-0.5 size-3.5 shrink-0",
          tone === "danger" ? "text-[#d6342f]" : tone === "success" ? "text-[#007955]" : "text-[#5c5551]",
        )}
      />
      <div className="min-w-0">
        <p className="text-xs font-semibold text-fg">{title}</p>
        <p className="text-[11.5px] text-fg-subtle">{detail}</p>
      </div>
    </div>
  );
}

function ConflictCard({
  conflict,
  records,
  fqdn,
  editRequires,
  onRemove,
}: {
  conflict: DnsConflict;
  records: DnsRecord[];
  fqdn: string;
  editRequires: Requirement | null;
  onRemove: (records: DnsRecord[], kept: DnsRecord) => void;
}) {
  const keep = records.find((r) => r.id === conflict.keep_id) ?? records[0]!;
  const losers = records.filter((r) => r.id !== keep.id);
  const label = conflict.name === "@" ? fqdn : conflict.name;
  const count = records.length === 2 ? "two" : String(records.length);

  const alt = alternative(conflict, records, keep, losers);

  const website =
    conflict.name === "@" || conflict.name === "www" || records.some((r) => r.managed_by === "website");
  const email = records.some(touchesEmail);

  return (
    <div className="flex flex-col gap-3.5 rounded-[14px] border border-[#ffc4bb] bg-[#fff1ee] p-4">
      <div className="flex flex-wrap items-center gap-2">
        <AlertTriangle aria-hidden className="size-4 shrink-0 text-[#d6342f]" />
        <p className="min-w-0 flex-1 text-sm font-semibold text-fg">
          {label} has {count} records that conflict
        </p>
        <Badge size="md" tone="danger">
          Needs fix
        </Badge>
      </div>
      <p className="text-[13px] text-[#5c5551]">{conflict.message}</p>
      <div className="overflow-x-auto rounded-[10px] border border-border bg-surface">
        <Table className="w-full min-w-[520px] text-left text-xs">
          <TableHead className="text-[10.5px] tracking-wide text-[#766e6a] uppercase">
            <TableRow>
              <TableHeaderCell className="px-3 py-2 font-medium">Type</TableHeaderCell>
              <TableHeaderCell className="px-3 py-2 font-medium">Host</TableHeaderCell>
              <TableHeaderCell className="px-3 py-2 font-medium">Value</TableHeaderCell>
              <TableHeaderCell className="px-3 py-2 font-medium">TTL</TableHeaderCell>
              <TableHeaderCell className="px-3 py-2 font-medium">If you fix it</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((r) => {
              const removed = r.id !== keep.id;
              return (
                <TableRow key={r.id} className={cx("border-t border-border", removed && "bg-[#fffaf8]")}>
                  <TableCell className="px-3 py-2.5 text-fg">{r.type}</TableCell>
                  <TableCell className="px-3 py-2.5 text-fg">{r.name}</TableCell>
                  <TableCell className={cx("px-3 py-2.5 break-all text-fg", removed && "line-through")}>
                    {recordValue(r)}
                  </TableCell>
                  <TableCell className={cx("px-3 py-2.5 text-fg", removed && "line-through")}>
                    {r.ttl}
                  </TableCell>
                  <TableCell
                    className={cx(
                      "px-3 py-2.5 font-medium whitespace-nowrap",
                      removed ? "text-[#b52522]" : "text-[#007955]",
                    )}
                  >
                    {removed ? "Removed" : "Kept - goes live"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-[10.5px] font-semibold tracking-wide text-[#766e6a] uppercase">
          What this affects
        </p>
        <ImpactLine
          icon="website"
          tone={website ? "danger" : "success"}
          title={website ? `Website: ${fullHost(conflict.name, fqdn)}` : "Website"}
          detail={
            website
              ? `Right now some visitors may reach the wrong place. Fixing this sends them to ${keep.content}.`
              : "No change. Website records aren't touched."
          }
        />
        <ImpactLine
          icon="email"
          tone={email ? "danger" : "success"}
          title="Email"
          detail={
            email
              ? "Mail may be delivered or checked inconsistently until this is fixed."
              : "No change. Email records aren't touched."
          }
        />
      </div>
      {editRequires && (
        <div className="flex flex-wrap items-center gap-2.5">
          <Button size="lg" variant="danger" requires={editRequires} onClick={() => onRemove(losers, keep)}>
            Review and remove{" "}
            {conflict.kind === "duplicate"
              ? "the duplicate"
              : losers.length > 1
                ? "old records"
                : "old record"}
          </Button>
          {alt && (
            <Button
              size="lg"
              variant="secondary"
              requires={editRequires}
              onClick={() => onRemove(alt.remove, alt.keep)}
            >
              {alt.label}
            </Button>
          )}
          <span className="flex flex-1 items-center justify-end gap-1.5 text-[11px] text-fg-subtle">
            <Lock aria-hidden className="size-3" />
            You'll confirm the change before it's applied
          </span>
        </div>
      )}
    </div>
  );
}

/** The other way round: keep one of the records the suggested fix would remove. */
function alternative(
  conflict: DnsConflict,
  records: DnsRecord[],
  keep: DnsRecord,
  losers: DnsRecord[],
): { keep: DnsRecord; remove: DnsRecord[]; label: string } | null {
  if (conflict.kind === "duplicate_cname" && losers[0]) {
    const other = losers[0];
    return {
      keep: other,
      remove: records.filter((r) => r.id !== other.id),
      label: `Keep ${other.content} instead`,
    };
  }
  if (conflict.kind === "cname_with_other") {
    const other =
      keep.type === "CNAME"
        ? records.find((r) => r.type !== "CNAME")
        : records.find((r) => r.type === "CNAME");
    if (!other) return null;
    return {
      keep: other,
      remove: records.filter((r) => (other.type === "CNAME" ? r.type !== "CNAME" : r.type === "CNAME")),
      label: `Keep ${other.type} record instead`,
    };
  }
  return null;
}
